/**
 * TagPicker — поповер выбора тегов с поиском и create-on-type
 * («+ Создать тег …»). Общий для каталогов макетов и товаров.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { cn } from '../../lib/cn'
import { nextTagColor, tagDotClass, type CatalogTag } from './tags'

interface TagPickerProps<T extends CatalogTag> {
  tags: T[]
  /** id отмеченных тегов */
  selectedIds: number[]
  onToggle: (tag: T, checked: boolean) => void
  /** Создание тега (create-on-type). Возвращает созданный тег. */
  onCreate: (value: string, color: string) => Promise<T | null>
  /** Кнопка-триггер */
  trigger: (open: () => void) => React.ReactNode
  /** Открывать меню вверх (для bulk-панели) */
  openUp?: boolean
  hint?: string
}

export function TagPicker<T extends CatalogTag>({
  tags,
  selectedIds,
  onToggle,
  onCreate,
  trigger,
  openUp = false,
  hint,
}: TagPickerProps<T>) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tags
    return tags.filter((t) => t.value.toLowerCase().includes(q))
  }, [tags, query])

  const canCreate =
    query.trim().length > 0 &&
    !tags.some((t) => t.value.toLowerCase() === query.trim().toLowerCase())

  const handleCreate = async () => {
    if (!canCreate || creating) return
    setCreating(true)
    try {
      const tag = await onCreate(query.trim(), nextTagColor(tags.length))
      if (tag) {
        onToggle(tag, true)
        setQuery('')
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <div ref={rootRef} className="relative inline-block">
      {trigger(() => setOpen((v) => !v))}
      {open && (
        <div
          className={cn(
            'absolute left-0 z-50 min-w-[13rem] max-h-64 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-medium dark:border-zinc-700 dark:bg-zinc-900',
            openUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          <div className="mx-2 mb-1.5 mt-1 flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 dark:border-zinc-700">
            <Search className="h-3.5 w-3.5 text-gray-400 dark:text-zinc-500" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleCreate()
              }}
              placeholder="Найти или создать тег…"
              className="w-full bg-transparent text-xs text-gray-900 outline-none placeholder:text-gray-400 dark:text-zinc-100 dark:placeholder:text-zinc-500"
            />
          </div>
          {filtered.length === 0 && !canCreate && (
            <div className="px-3 py-2 text-xs text-gray-400 dark:text-zinc-500">Тегов пока нет</div>
          )}
          {filtered.map((t) => {
            const checked = selectedIds.includes(t.id)
            return (
              <label
                key={t.id}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(t, !checked)}
                  className="h-3.5 w-3.5 accent-indigo-600"
                />
                <span
                  className={cn('inline-block h-2 w-2 shrink-0 rounded-full', tagDotClass(t.color))}
                />
                <span className="flex-1 truncate">{t.value}</span>
                {typeof t.count === 'number' && (
                  <span className="text-2xs tabular-nums text-gray-400 dark:text-zinc-500">
                    {t.count}
                  </span>
                )}
              </label>
            )
          })}
          {canCreate && (
            <button
              type="button"
              disabled={creating}
              onClick={() => void handleCreate()}
              className="block w-full border-t border-gray-100 px-3 py-1.5 text-left text-xs text-indigo-600 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-800 dark:text-indigo-400 dark:hover:bg-zinc-800"
            >
              + Создать тег «{query.trim()}»
            </button>
          )}
          {hint && (
            <div className="border-t border-gray-100 px-3 py-1.5 text-2xs text-gray-400 dark:border-zinc-800 dark:text-zinc-500">
              {hint}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TagPicker
